import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ProjectSettingDialog } from '@web/app/features/home/components';
import { useProjectService, useProjectSettingDialog, useWorkspaceService } from '@web/app/features/home/hooks';
import { useCreateProjectDialog, useMyRole, useSelectedStore } from '@web/app/shared';
import { EllipsisVertical, Plus, RefreshCw } from 'lucide-react';

import { ListEmptyImage } from '@codes/assets';
import { Button, Skeleton } from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

export const ProjectListPage = () => {
    const { workspaceFromServerQueryData, workspaceFromServer } = useWorkspaceService();
    const { myProjectsQueryResult } = useProjectService();
    const { setProject, workspace } = useSelectedStore();
    const navigate = useNavigate();

    const { setProjectDialog } = useCreateProjectDialog({});
    const { open } = useProjectSettingDialog();
    const { t } = useTranslation();

    const myRole = useMyRole();
    const isAccessable = myRole <= 1;

    if (workspaceFromServerQueryData.isFetching && !workspaceFromServer) {
        return (
            <div className="flex flex-col py-8 px-[68px] max-md:px-5 w-full h-full">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                </div>
            </div>
        );
    }

    if (!workspaceFromServer) {
        return null;
    }

    const isRequested = workspaceFromServer.state === 'requested';

    if (isRequested) {
        return (
            <div className="flex flex-col gap-y-20 py-[34px] px-[68px] max-md:px-4 max-md:p-5 w-full h-full">
                <div className="flex items-center justify-end mb-[42px]">
                    <Button
                        onClick={() => workspaceFromServerQueryData.refetch()}
                        className="rounded-full pl-[19px] gap-[6px]"
                        disabled={workspaceFromServerQueryData.isRefetching}
                    >
                        <RefreshCw
                            size={16}
                            className={workspaceFromServerQueryData.isRefetching ? 'animate-spin' : ''}
                        />
                        {t('box:editorPage.load.refresh')}
                    </Button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center translate-y-[-50px] ">
                    <div className="flex gap-2 mb-6">
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce [animation-delay:0ms]"></div>
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce [animation-delay:150ms]"></div>
                        <div className="w-3 h-3 bg-primary rounded-full animate-bounce [animation-delay:300ms]"></div>
                    </div>
                    <div className="text-[24px] font-medium text-gray-900 mb-3">
                        {t('workspace.preparingMessageText')}
                    </div>
                    <div className="text-[16px] text-gray-600 text-center max-w-md leading-relaxed">
                        {t('workspace.preparingMessageDesc')}
                    </div>
                </div>
            </div>
        );
    }

    const isLoading = myProjectsQueryResult.isFetching || myProjectsQueryResult.isRefetching;

    if (isLoading) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                </div>
            </div>
        );
    }

    if (myProjectsQueryResult.error) {
        return (
            <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full">
                <div className="flex gap-x-2 flex-col items-center gap-y-4">
                    <div className="text-error">{t('project.error.fetchList')}</div>
                    <Button
                        className="text-sm p-2 h-auto"
                        onClick={() => myProjectsQueryResult.refetch()}
                        disabled={myProjectsQueryResult.isRefetching}
                    >
                        {t('project.error.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    const isEmpty = myProjectsQueryResult.data?.data.length === 0;

    if (isEmpty) {
        return (
            <div className="w-full flex-1 flex flex-col justify-center py-[34px] px-[68px] max-md:p-5">
                <div className="text-[22px] font-semibold">Projects</div>
                <div className="flex-1 flex flex-col justify-center items-center translate-y-[-75px]">
                    <Image className="max-w-[140px]" src={ListEmptyImage} />
                    <div className="text-[26px] font-medium text-gray-black mb-3 mt-[19px]">
                        {t('project.emptyState.title')}
                    </div>
                    <div className="text-[20px] font-medium text-gray-700 mb-[22px] text-center">
                        {t('project.emptyState.description')}
                    </div>
                    <Button onClick={() => setProjectDialog(true)} className="h-11 rounded-full pl-[19px] gap-[6px]">
                        <Plus size={16} /> {t('project.addProject')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col py-[34px] px-[68px] max-md:p-5 w-full">
            <div className="flex items-center justify-between mb-[42px]">
                <div className="text-[22px] font-semibold">Projects</div>
                <Button onClick={() => setProjectDialog(true)} className="h-11 rounded-full pl-[19px] gap-[6px]">
                    <Plus size={16} /> {t('project.addProject')}
                </Button>
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                {myProjectsQueryResult.data?.data.map(project => (
                    <Card
                        onClick={() => {
                            setProject(project);
                            if (workspace?.id) {
                                navigate(`/w/${workspace.id}/p/${project.id}`);
                            }
                        }}
                        className="cursor-pointer bg-gray-50 rounded-[18px]"
                        key={project.id}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-gray-700 text-[15px]">
                                {formatDate(project.createdAt, 'YYYY. MM. DD')}
                            </div>
                            {isAccessable && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className=" p-[2px] group w-8 h-8">
                                            <EllipsisVertical className="w-4 h-4 group-hover:text-text transition-colors duration-200" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="center" className="w-[100px] p-1">
                                        <DropdownMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                open(project.id as string);
                                            }}
                                        >
                                            Setting
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>

                        <div className="border-b border-detail-line my-[14px]"></div>
                        <div className="truncate text-lg font-medium">{project.name}</div>
                    </Card>
                ))}
            </div>
            <ProjectSettingDialog />
        </div>
    );
};
